import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import NavigateBeforeOutlinedIcon from "@mui/icons-material/NavigateBeforeOutlined";
import NavigateNextOutlinedIcon from "@mui/icons-material/NavigateNextOutlined";
import { Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../components/CustomButton";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import {
  addTaskByPatientId,
  deleteTasksByIds,
  getInpatientById,
  getInpatientTasksByAppointmentId,
  updatePatientTaskActiveStatusById,
  updatePatientTaskStatusById,
  updateTaskByPatientId,
} from "../../../../redux/reducers/clinicSlice";
import {
  createProcedureWoLoader,
  getPetsCompliantSummaryWoLoader,
  updateProcedureByIdWoLoader,
} from "../../../../redux/reducers/petSlice";
import { showSnackBar } from "../../../../redux/reducers/snackSlice";
import { AppColors } from "../../../../util/AppColors";
import { frequncyCheckList } from "../../../../util/arrayList";
import { routeList, typeOptions } from "../../../../util/dropList";
import { billingTabEnum } from "../../../../util/enum";
import {
  createTreatmentPlanTasks,
  getActualTasks,
  getDoneAtFromNextDueDate,
  getProcedureDetailsNew,
  getRightSaluation,
} from "../../../../util/function";
import { canWriteModule } from "../../../../util/roleModuleAccess";
import "./TreatmentPlan.css";

const defObj = { services: [], stocks: [], facilities: [], locations: [] };
const delBtn = { color: AppColors.red, width: 20, height: 20 };
const edtBtn = { color: AppColors.appPrimary, width: 20, height: 20 };
const durationTypeOptions = [
  { name: "Days", value: "Days" },
  { name: "Until discontinued", value: "Until discontinued" },
];

const ADMIN_CATEGORIES = [
  "deworming",
  "diet",
  "flea & tick treatment",
  "fluid therapy",
  "grooming & hygiene care",
  "medication",
  "nutrition and supplements",
  "other parasite treatment",
  "vaccines",
];

const mapCategoryLabel = (item) => {
  if (item?.type) return item.type;
  const cat = String(item?.category || "").toLowerCase();
  if (cat === "procedure") return "Procedure";
  if (cat === "others") return "Monitoring Check";
  if (cat === "adminster") return "Medication";
  if (cat === "diagnostics") return "Diagnostics";
  return item?.category || "—";
};

const buildItemDetails = (item) => {
  const parts = [];
  if (item?.dose) parts.push(item.dose);
  if (item?.rate) parts.push(item.rate);
  if (item?.amountPerFeeding) parts.push(item.amountPerFeeding);
  if (item?.route) parts.push(item.route);
  if (item?.frequency) parts.push(item.frequency);
  else if (item?.noOfTimes || item?.times) {
    parts.push(`${item.noOfTimes || item.times}x/day`);
  }
  if (item?.startDate && item?.startTime) {
    parts.push(
      `Started ${moment(`${item.startDate}T${item.startTime}`).format(
        "DD MMM YYYY, HH:mm",
      )}`,
    );
  }
  if (item?.durationType === "Till Discharge") {
    parts.push("Until discontinued");
  } else if (item?.durationLabel) {
    parts.push(item.durationLabel);
  } else if (item?.duration) {
    parts.push(`${item.duration} day(s)`);
  }
  if (item?.notes) parts.push(item.notes);
  return parts.join(" · ");
};

const getScheduleStatus = (task, selectedDate = new Date()) => {
  if (String(task?.status) === "true") return "GIVEN";
  if (String(task?.status) === "false") return "NOT_DONE";

  const [hour, min] = String(task?.startTime || "00:00").split(":");
  const taskDateTime = new Date(
    new Date(selectedDate).setHours(Number(hour) || 0, Number(min) || 0, 0, 0),
  );

  const isTimeExceed = new Date().getTime() > taskDateTime.getTime();
  return isTimeExceed ? "OVERDUE" : "DUE";
};

const getNameLabel = (type) => {
  switch (type) {
    case "Fluid Therapy":
      return "FLUID TYPE";
    case "Diet":
      return "DIET / FOOD TYPE";
    case "Procedure":
      return "PROCEDURE NAME";
    case "Monitoring Check":
      return "PARAMETER / CHECK NAME";
    case "Medication":
    default:
      return "DRUG NAME";
  }
};

const getAmountMeta = (type) => {
  switch (type) {
    case "Fluid Therapy":
      return {
        label: "RATE",
        placeholder: "e.g. 40 ml/hr",
        field: "rate",
      };
    case "Diet":
      return {
        label: "AMOUNT PER FEEDING",
        placeholder: "e.g. 1 can",
        field: "amountPerFeeding",
      };
    case "Medication":
      return {
        label: "DOSE",
        placeholder: "e.g. 15 mg/kg",
        field: "dose",
      };
    default:
      return null;
  }
};

const getInitAddForm = () => ({
  type: "Medication",
  name: "",
  stockId: null,
  serviceId: null,
  frequency: "Once daily",
  startDateTime: dayjs(),
  duration: "Until discontinued",
  durationDays: "",
  notes: "",
  tillDiscontinued: true,
  dose: "",
  route: "IV",
  rate: "",
  amountPerFeeding: "",
});

const TreatmentPlan = () => {
  const dispatch = useDispatch();
  const petAndApntData = useSelector(
    (state) => state?.pet?.petAndAppointmentDetails,
  );
  const {
    inpatientTaskList,
    inpatientDetails,
    details: clinicDet,
  } = useSelector((state) => state?.clinic);
  const clinicVets = useSelector((state) => state?.vet?.vets?.vets);
  const apntDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const profile = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);
  const canDeletePlan =
    profile?.id === petAndApntData?.inpatientResponse?.doctorId;
  const canManageDashboard = canWriteModule("dashboard");

  const [showAddModal, setShowAddModal] = useState(false);
  const [discontinueModVsble, setDiscontinueModVsble] = useState(false);
  const [deleteModVsble, setDeleteModVsble] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // "plan" | "subtask"
  const [selectedTask, setSelectedTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [remarksModVsble, setRemarksModVsble] = useState(false);
  const [remarks, setRemarks] = useState("");

  const [addForm, setAddForm] = useState(getInitAddForm);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const appointmentId =
    petAndApntData?.appointment?.appointmentId ??
    apntDet?.appointment?.appointmentId;

  const patientId =
    apntDet?.patientId ?? petAndApntData?.inpatientResponse?.patientId;

  const refreshSchedule = () => {
    if (!patientId) return;
    dispatch(
      getInpatientById({
        inpatientId: patientId,
        url: `?date=${moment(new Date(selectedDate)).format("YYYY-MM-DD")}`,
      }),
    );
  };

  const refreshTasks = () => {
    if (appointmentId) {
      dispatch(getInpatientTasksByAppointmentId(appointmentId));
    }
    refreshSchedule();
  };

  useEffect(() => {
    refreshTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId, selectedDate, patientId]);

  const handleDateChange = (value) => {
    const reqDate = new Date(selectedDate);
    reqDate.setDate(
      value === "add" ? reqDate.getDate() + 1 : reqDate.getDate() - 1,
    );
    setSelectedDate(reqDate);
  };

  const onChangeSelectedDate = (e) => {
    const { value } = e?.target ?? {};
    if (!value) return;
    const nextDate =
      typeof value?.toDate === "function" ? value.toDate() : new Date(value);
    if (!moment(nextDate).isValid()) return;
    setSelectedDate(nextDate);
  };

  const dateLabel = useMemo(
    () => moment(selectedDate).format("ddd, DD MMM YYYY"),
    [selectedDate],
  );

  const treatmentItems = useMemo(() => {
    return (inpatientTaskList ?? [])
      ?.filter((li) => li?.taskType?.toLowerCase() === "main")
      ?.map((item) => ({
        ...item,
        id: item.id,
        category: mapCategoryLabel(item),
        name: item.task || item.name || "—",
        details: buildItemDetails(item),
        isDiscontinued: item.active === false || item.isDiscontinued === true,
        raw: item,
      }));
  }, [inpatientTaskList]);

  const getDoneByName = (doneById) => {
    if (!doneById) return "";
    const vetMatch = clinicVets?.find((vet) => vet?.doctorId === doneById);
    if (vetMatch?.name) {
      return `${getRightSaluation(clinicVets, doneById)}${vetMatch.name}`;
    }
    if (clinicDet?.id === doneById) return clinicDet?.name ?? "";
    if (profile?.id === doneById) return profile?.name ?? "";
    return "";
  };

  const scheduleEntries = useMemo(() => {
    return getActualTasks(inpatientDetails?.tasks ?? [])
      ?.filter((li) => li?.active !== false)
      ?.map((task) => ({
        id: task.id,
        time: moment(`${task.startDate}T${task.startTime}`).isValid()
          ? moment(`${task.startDate}T${task.startTime}`).format("hh:mm a")
          : task.startTime || "—",
        name: task.task || "—",
        status: getScheduleStatus(task, selectedDate),
        doneBy: getDoneByName(task.doneBy),
        doneTime: task.doneTime || "",
        remark: task.remark || "",
        raw: task,
      }))
      ?.sort((a, b) =>
        String(a.raw?.startTime || "").localeCompare(
          String(b.raw?.startTime || ""),
        ),
      );
  }, [inpatientDetails?.tasks, selectedDate, clinicVets, clinicDet, profile]);

  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const {
    services,
    stocks: clinicStocks,
    facilities,
    locations,
  } = useMemo(() => {
    if (!mandateDataRaw) return defObj;
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        services: Array.isArray(reqData?.services) ? reqData.services : [],
        stocks: Array.isArray(reqData?.stocks) ? reqData.stocks : [],
        facilities: Array.isArray(reqData?.facilities)
          ? reqData.facilities
          : [],
        locations: Array.isArray(reqData?.locations) ? reqData.locations : [],
      };
    } catch {
      return defObj;
    }
  }, [mandateDataRaw]);

  const refreshComplaintSummary = () => {
    if (appointmentId) {
      dispatch(getPetsCompliantSummaryWoLoader(appointmentId));
    }
  };

  const getProcedureList = () =>
    services
      ?.filter(
        (li) =>
          li?.category?.toLowerCase() !== "radiology" &&
          li?.category?.toLowerCase() !== "lab",
      )
      ?.map((dta) => ({
        ...dta,
        name: dta?.serviceName,
        value: dta?.serviceName,
      }));

  const getStockList = (type) => {
    const typeKey = type?.toLowerCase();
    const reqList = clinicStocks?.filter((dta) => {
      const category = dta?.category?.toLowerCase();
      if (typeKey === "medication") {
        return (
          category === "medication" ||
          category === "deworming" ||
          category === "flea & tick treatment" ||
          category === "other parasite treatment" ||
          category === "vaccines" ||
          category === "nutrition and supplements"
        );
      }
      if (typeKey === "fluid therapy") {
        return category === "fluid therapy";
      }
      if (typeKey === "diet") {
        return category === "diet" || category === "nutrition and supplements";
      }
      return true;
    });
    return reqList?.map((li) => ({
      name: li?.tradeName,
      value: li?.tradeName,
      stockId: li?.stockId,
      type: li?.category,
      productName: li?.productName,
    }));
  };

  const onChangeForm = (name, value) => {
    setAddForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleTypeChange = (e) => {
    const nextType = e?.target?.value;
    setAddForm((prev) => ({
      ...prev,
      type: nextType,
      name: "",
      stockId: null,
      serviceId: null,
      dose: "",
      rate: "",
      amountPerFeeding: "",
      route: "IV",
    }));
  };

  const handleNameSelect = (e) => {
    setAddForm((prev) => ({
      ...prev,
      name: e?.value ?? e ?? "",
      stockId: e?.stockId ?? null,
      serviceId: e?.serviceId ?? null,
    }));
  };

  const onDiscontinue = (itm) => {
    setSelectedTask(itm);
    setDiscontinueModVsble(true);
  };

  const onDeletePlan = (itm) => {
    if (!canDeletePlan) return;
    setSelectedTask(itm);
    setDeleteType("plan");
    setDeleteModVsble(true);
  };

  const onDeleteSubTask = (entry) => {
    if (!canDeletePlan) return;
    setSelectedTask(entry?.raw || entry);
    setDeleteType("subtask");
    setDeleteModVsble(true);
  };

  const onCloseDeleteMod = () => {
    setDeleteModVsble(false);
    setDeleteType(null);
    setSelectedTask(null);
  };

  const onConfirmDelete = () => {
    if (!selectedTask?.id) {
      onCloseDeleteMod();
      return;
    }

    if (deleteType === "subtask") {
      dispatch(deleteTasksByIds({ ids: [selectedTask.id] })).then((res) => {
        if (res?.payload) {
          dispatch(
            showSnackBar({
              message: "Task deleted successfully",
              type: "success",
            }),
          );
          refreshTasks();
          onCloseDeleteMod();
        }
      });
      return;
    }

    const subTaskList = (inpatientTaskList ?? [])?.filter(
      (st) => st?.mainTaskId === selectedTask?.id,
    );
    // Delete pending subtasks only (status !== "true"), plus the main task
    const pendingIds =
      subTaskList
        ?.filter((st) => String(st?.status) !== "true")
        ?.map((st) => st?.id)
        ?.filter(Boolean) ?? [];

    const idsToDelete = [selectedTask.id, ...pendingIds];

    dispatch(deleteTasksByIds({ ids: idsToDelete })).then((res) => {
      if (res?.payload) {
        refreshTasks();
        onCloseDeleteMod();
      }
    });
  };

  const mapItemToAddForm = (item) => {
    const type = item?.type || mapCategoryLabel(item) || "Medication";
    const start =
      item?.startDate && item?.startTime
        ? dayjs(`${item.startDate}T${item.startTime}`)
        : dayjs();

    const isTillDiscontinued =
      item?.durationType === "Till Discharge" ||
      item?.tillDiscontinued === true ||
      item?.durationLabel === "Until discontinued";

    let durationDays = "";
    if (!isTillDiscontinued) {
      const fromLabel = String(item?.durationLabel || "").match(/(\d+)/);
      const days = Number(item?.duration) || Number(fromLabel?.[1]) || 0;
      durationDays = days > 0 ? String(days) : "";
    }

    return {
      type,
      name: item?.task || item?.name || "",
      stockId: item?.stockId ?? null,
      serviceId: item?.serviceId ?? null,
      frequency: item?.frequency || "Once daily",
      startDateTime: start?.isValid?.() ? start : dayjs(),
      duration: isTillDiscontinued ? "Until discontinued" : "Days",
      durationDays,
      notes: item?.notes || "",
      tillDiscontinued: isTillDiscontinued,
      dose: item?.dose || "",
      route: item?.route || "IV",
      rate: item?.rate || "",
      amountPerFeeding: item?.amountPerFeeding || "",
    };
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setEditingTask(null);
    setAddForm(getInitAddForm());
  };

  const handleEditPlan = (item) => {
    if (!item) return;
    setEditingTask(item);
    setAddForm(mapItemToAddForm(item));
    setShowAddModal(true);
  };

  const onCloseDiscontinueMod = () => {
    setDiscontinueModVsble(false);
    setSelectedTask(null);
  };

  const onConfirmDiscontinue = () => {
    if (!selectedTask?.id) {
      onCloseDiscontinueMod();
      return;
    }

    const subTaskList = (inpatientTaskList ?? [])?.filter(
      (st) => st?.mainTaskId === selectedTask?.id,
    );
    const now = new Date();
    const upcomingList =
      subTaskList
        ?.filter((itm) => {
          if (String(itm?.status) === "true") return false;
          const taskDateTime = new Date(`${itm?.startDate}T${itm?.startTime}`);
          return taskDateTime > now;
        })
        ?.map((itm) => itm?.id) ?? [];

    if (upcomingList?.length > 0) {
      dispatch(deleteTasksByIds({ ids: upcomingList }));
    }

    dispatch(
      updatePatientTaskActiveStatusById({
        taskId: selectedTask?.id,
        url: `?active=${false}`,
      }),
    ).then((res) => {
      if (res?.payload) {
        refreshTasks();
        onCloseDiscontinueMod();
      }
    });
  };

  const createProcedureFromTask = async (itm) => {
    if (!itm?.serviceId) return;

    const reqServ = services?.find((ser) => ser?.serviceId === itm?.serviceId);
    if (!reqServ) return;

    const clinicVariants = reqServ?.serviceVariants?.filter(
      (vl) => vl?.createdBy === clinicDet?.id && vl?.approved,
    );
    const reqVar = clinicVariants?.[0];
    const reqQty = 1;

    const procedureDetailsResult = await getProcedureDetailsNew({
      servId: reqServ?.serviceId,
      varntId: reqVar?.variantId,
      serviceName: reqServ?.serviceName ?? itm?.task,
      services: services ?? [],
      clinicStocks: clinicStocks ?? [],
      pharmacyTaxId: clinicDet?.pharmacyTaxId,
      status: true,
      facilities: facilities ?? [],
      facilityId: apntDet?.appointment?.facilityId ?? null,
      patientWorkbook: true,
      locations: locations ?? [],
      clinicId: clinicDet?.id,
    });
    const procedureDetails = procedureDetailsResult?.procedureDetails;

    let tax = 0;
    let total = reqVar?.servicePrice ?? 0;
    if (reqServ?.taxApplicability?.toLowerCase() === "yes") {
      tax +=
        (Number(reqVar?.servicePrice ?? 0) / 100) * Number(reqServ?.tax ?? 0);
      total += tax;
    }

    const data = {
      procedureName: reqServ?.serviceName,
      serviceId: reqServ?.serviceId,
      category: reqServ?.category,
      subCategory: reqServ?.subCategory,
      remarks: reqServ?.remarks ?? "",
      doneAt: moment(new Date()).format("YYYY-MM-DD"),
      status: "Pending",
      petId: apntDet?.appointment?.petId ?? petAndApntData?.pet?.petId,
      appointmentId:
        apntDet?.appointment?.appointmentId ?? appointmentId ?? null,
      serviceCharge: reqVar?.servicePrice ?? 0,
      serviceType: reqVar?.variantName,
      procedureDetails,
      tax: Number(Number(tax) * Number(reqQty)) ?? 0,
      total: Number(Number(total) * Number(reqQty)) ?? 0,
      mrpPrice: Number(reqVar?.servicePrice ?? 0),
      procedureVariantId: reqVar?.variantId,
      quantity: Number(reqQty),
      patientWorkbook: true,
      mainProcedure: true,
      hsnSacCode: reqServ?.hsnSacCode ?? null,
      paymentId: apntDet?.paymentId ?? null,
    };

    const res = await dispatch(createProcedureWoLoader({ data }));
    if (res?.payload) refreshComplaintSummary();
  };

  const addStockToExistingProcedure = async (itm) => {
    const reqProced = apntDet?.procedures?.[0];
    if (!reqProced?.procedureId || !itm?.stockId) return;

    const reqStock = clinicStocks?.find((stc) => stc?.stockId === itm?.stockId);
    if (!reqStock) return;

    const stockCategory = (reqStock?.category ?? "").toLowerCase();
    const isAdmin = ADMIN_CATEGORIES.includes(stockCategory);

    const reqServ = services?.find(
      (ser) => ser?.serviceId === reqProced?.serviceId,
    );
    const reqVar =
      reqServ?.serviceVariants?.find(
        (vnt) =>
          vnt?.variantId === reqProced?.procedureVariantId ||
          (vnt?.variantName ?? "")?.toLowerCase() ===
            (reqProced?.serviceType ?? "")?.toLowerCase(),
      ) ?? reqServ?.serviceVariants?.[0];

    const clinicBatches = reqStock?.stockBatch?.filter(
      (btch) => btch?.createdBy === clinicDet?.id && btch?.approved,
    );
    const reqBat = clinicBatches?.[0] ?? null;
    const currLocat = locations?.find((loc) => loc?.id === reqBat?.locationId);
    const isLinked = true;
    const isNoAmount =
      reqStock?.autoConsumed || !reqStock?.billable || isLinked;
    const reqUnitPrice = isNoAmount ? 0 : (reqBat?.unitSellingPrice ?? 0);
    const taxPerent =
      reqServ?.taxApplicability?.toLowerCase() === "yes"
        ? Number(reqBat?.cgst ?? 0) + Number(reqBat?.sgst ?? 0)
        : 0;
    const reqPercent = 100 + Number(taxPerent);
    const quantity = 1;
    const reqPrice = Number(reqUnitPrice) * quantity;
    const unitPrice = Number(reqPrice) / (reqPercent / 100);
    const prdTaxValue = (taxPerent / 100) * Number(unitPrice);
    const nextDueDate =
      isAdmin &&
      isLinked &&
      stockCategory === "vaccines" &&
      reqServ?.nextDueDate?.length > 0
        ? getDoneAtFromNextDueDate(reqServ?.nextDueDate)
        : null;
    const uomList =
      reqStock?.uomMappings
        ?.filter((ml) => ml?.allowSale === "Y" && ml?.show)
        ?.map((ml) => ({
          name: ml?.unit,
          value: ml?.unit,
          conversion: ml?.conversion,
          uomMappingId: ml?.uomMappingId ?? null,
        })) ?? [];
    const defaultUom = uomList?.[0];
    const itemName =
      reqStock?.tradeName ?? reqStock?.productName ?? itm?.task ?? "";

    const procedureDetails = [
      {
        detailId: null,
        stockId: itm?.stockId ?? null,
        batchId: reqStock?.autoConsumed ? reqBat?.batchId : null,
        type: isAdmin ? "Administered" : "Consumables",
        quantity,
        status: true,
        remarks: "Completed",
        estimatedCost: isNoAmount ? 0 : reqPrice,
        name: itemName,
        procedureName: itemName,
        dosage: isAdmin ? (itm?.dose ?? null) : undefined,
        route: isAdmin ? (itm?.route ?? null) : undefined,
        isDelete: false,
        serviceId: reqProced?.serviceId ?? null,
        tax: isNoAmount ? 0 : Number(prdTaxValue ?? 0),
        total: isNoAmount ? 0 : Number(reqPrice ?? 0),
        mrpPrice: isNoAmount ? 0 : Number(reqUnitPrice ?? 0),
        procedureVariantId: reqProced?.procedureVariantId ?? reqVar?.variantId,
        patientWorkbook: true,
        mainProcedure: false,
        drugSchedule: reqServ?.drugSchedule ?? null,
        hsnSacCode: reqServ?.hsnSacCode ?? null,
        packageUom: reqBat?.salesUom ?? null,
        expiryDate: reqBat?.expiryDate ?? null,
        batchNo: reqBat?.batchNo ?? null,
        billable: reqStock?.billable,
        autoConsumed: reqStock?.autoConsumed,
        linked: "Y",
        billType: reqStock?.autoConsumed
          ? currLocat?.category
            ? billingTabEnum?.[currLocat?.category]
            : "treatment"
          : "treatment",
        nextDueDate,
        billSeparated: false,
        uomId: defaultUom?.uomMappingId ?? null,
        uomName: defaultUom?.value ?? null,
        billing: false,
      },
    ];

    let tax = 0;
    let total = reqVar?.servicePrice ?? reqProced?.serviceCharge ?? 0;
    if (reqServ?.taxApplicability?.toLowerCase() === "yes") {
      tax +=
        (Number(reqVar?.servicePrice ?? 0) / 100) * Number(reqServ?.tax ?? 0);
      total += tax;
    }

    const res = await dispatch(
      updateProcedureByIdWoLoader({
        id: reqProced?.procedureId,
        data: {
          serviceCharge: reqProced?.serviceCharge,
          serviceType: reqProced?.serviceType,
          appointmentId: apntDet?.appointment?.appointmentId ?? appointmentId,
          procedureDetails,
          tax: Number(Number(tax) * Number(reqProced?.quantity ?? 1)),
          total: Number(Number(total) * Number(reqProced?.quantity ?? 1)),
          mrpPrice: Number(reqVar?.servicePrice ?? 0),
          procedureVariantId:
            reqVar?.variantId ?? reqProced?.procedureVariantId,
          quantity: Number(reqProced?.quantity ?? 1),
          hsnSacCode: reqServ?.hsnSacCode ?? null,
          paymentId: apntDet?.paymentId ?? null,
        },
      }),
    );
    if (res?.payload) refreshComplaintSummary();
  };

  const updateTaskStatus = async (itm, status, remarks = "") => {
    if (!itm?.id) return;

    dispatch(
      updatePatientTaskStatusById({
        taskId: itm?.id,
        url: `?status=${status}&remark=${remarks}`,
      }),
    ).then(async (res) => {
      if (res?.payload) {
        refreshTasks();
      }
    });
  };

  const onCloseRemarksMod = () => {
    setRemarksModVsble(false);
    setRemarks("");
    setSelectedTask(null);
  };

  const handleTaskCancel = (itm) => {
    setSelectedTask(itm);
    setRemarksModVsble(true);
  };

  const onSaveRemarks = () => {
    updateTaskStatus(selectedTask, false, remarks);
    onCloseRemarksMod();
  };

  const handleAddTreatmentPlan = () => {
    if (!(addForm.name || "").trim()) return;

    const tillDiscontinued = addForm.duration === "Until discontinued";
    const days = Number(addForm.durationDays);
    if (!tillDiscontinued && (!days || days < 1)) return;

    const durationLabel = tillDiscontinued
      ? "Until discontinued"
      : `${days} day${days === 1 ? "" : "s"}`;

    const { planListForApi } = createTreatmentPlanTasks({
      ...addForm,
      duration: durationLabel,
      durationDays: tillDiscontinued ? 0 : days,
      tillDiscontinued,
      services,
      appointmentId: appointmentId ?? null,
      ...(editingTask?.id ? { mainTaskId: editingTask.id } : {}),
    });

    if (
      !petAndApntData?.inpatientResponse?.patientId ||
      !planListForApi?.length
    ) {
      return;
    }

    // Edit flow: drop upcoming subtasks, then update via tasks/update with mainTaskId
    if (editingTask?.id) {
      planListForApi[0] = {
        ...planListForApi[0],
        active: editingTask.active !== false,
      };

      const subTaskList = (inpatientTaskList ?? [])?.filter(
        (st) => st?.mainTaskId === editingTask?.id,
      );
      const now = new Date();
      const upcomingList =
        subTaskList
          ?.filter((itm) => {
            if (String(itm?.status) === "true") return false;
            const taskDateTime = new Date(
              `${itm?.startDate}T${itm?.startTime}`,
            );
            return taskDateTime > now;
          })
          ?.map((itm) => itm?.id) ?? [];

      const saveEditedPlan = () => {
        dispatch(
          updateTaskByPatientId({
            patientId: petAndApntData?.inpatientResponse?.patientId,
            data: planListForApi,
          }),
        ).then((res) => {
          if (res?.payload) {
            refreshTasks();
            closeAddModal();
          }
        });
      };

      if (upcomingList?.length > 0) {
        dispatch(deleteTasksByIds({ ids: upcomingList })).then((deleteRes) => {
          if (deleteRes?.payload) saveEditedPlan();
        });
        return;
      }

      saveEditedPlan();
      return;
    }

    dispatch(
      addTaskByPatientId({
        patientId: petAndApntData?.inpatientResponse?.patientId,
        data: planListForApi,
      }),
    ).then((res) => {
      if (res?.payload) {
        refreshTasks();
        closeAddModal();
      }
    });
  };

  const renderNameField = () => {
    if (addForm.type === "Monitoring Check") {
      return (
        <CustomTextField
          fullWidth
          name="name"
          value={addForm.name}
          handleChange={(e) => onChangeForm("name", e?.target?.value)}
        />
      );
    }

    const list =
      addForm.type === "Procedure"
        ? getProcedureList()
        : getStockList(addForm.type);

    return (
      <CustomSelect
        list={list}
        value={addForm.name}
        addNewSelect
        handleChange={handleNameSelect}
      />
    );
  };

  const renderTypeSpecificFields = () => {
    const nameLabel = getNameLabel(addForm.type);
    const amountMeta = getAmountMeta(addForm.type);

    return (
      <>
        <div style={{ marginBottom: 14 }}>
          <label className="modal-field-label">{nameLabel}</label>
          {renderNameField()}
          {/* {addForm.type !== "Monitoring Check" ? (
            <p className="modal-helper-text">
              Picking a name from the list links this order to the inventory
              master — that's what lets it bill and tag (e.g. controlled
              substance) correctly once given.
            </p>
          ) : null} */}
        </div>

        {amountMeta ? (
          <div style={{ marginBottom: 14 }}>
            <label className="modal-field-label">{amountMeta.label}</label>
            <CustomTextField
              fullWidth
              name={amountMeta.field}
              placeholder={amountMeta.placeholder}
              value={addForm[amountMeta.field] ?? ""}
              handleChange={(e) =>
                onChangeForm(amountMeta.field, e?.target?.value)
              }
            />
          </div>
        ) : null}

        {addForm.type === "Medication" || addForm.type === "Fluid Therapy" ? (
          <div style={{ marginBottom: 14 }}>
            <label className="modal-field-label">ROUTE</label>
            <CustomSelect
              select
              list={routeList}
              value={addForm.route}
              handleChange={(e) => onChangeForm("route", e?.target?.value)}
            />
          </div>
        ) : null}
      </>
    );
  };

  return (
    <div className="tp-wrapper scroll-80vh w100Per">
      <div className="tp-header-row">
        <h2 className="tp-section-title">Treatment plan items</h2>
        {canManageDashboard ? (
          <div>
            <CustomButton
              text="+ Add to treatment plan"
              onClick={() => {
                setEditingTask(null);
                setAddForm(getInitAddForm());
                setShowAddModal(true);
              }}
              smallBtn
            />
          </div>
        ) : null}
      </div>

      <div className="tp-items-list">
        {treatmentItems.map((item) => {
          const subItems = (inpatientTaskList ?? [])?.filter(
            (st) => st?.mainTaskId === item?.id,
          );
          const isCompleted =
            subItems?.length > 0 &&
            subItems?.every((st) => String(st?.status) === "true");
          // Edit Plan only when the item is active and still has pending work
          const hasPendingTask = !!item?.active && !isCompleted;
          return (
            <div
              key={item.id}
              className={`tp-item-card ${item.isDiscontinued ? "is-discontinued" : ""}`}
            >
              <div className="tp-item-main">
                <div className="tp-item-header">
                  <span className="tp-cat-badge">{item.category}</span>
                  <span className="tp-item-name">{item.name}</span>
                  {item.isDiscontinued && (
                    <span className="tp-discontinued-tag">(discontinued)</span>
                  )}
                </div>
                <div className="tp-item-details">{item.details}</div>
              </div>
              {isCompleted ? (
                <span className="badge-given">Completed</span>
              ) : hasPendingTask ? (
                <div className="flex-row-ali-cen" style={{ gap: 12 }}>
                  {canManageDashboard ? (
                    <CustomButton
                      text="Discontinue"
                      smallBtn
                      redBtn
                      onClick={() => onDiscontinue(item)}
                    />
                  ) : null}
                  {canManageDashboard ? (
                    <div
                      className="cursor"
                      onClick={() => handleEditPlan(item)}
                    >
                      <div className="flex-center">
                        <EditIcon sx={edtBtn} />
                      </div>
                      <Typography className="blue-color fs8 txt-semi-bold text-align-center">
                        Edit
                      </Typography>
                    </div>
                  ) : null}
                  {canManageDashboard && canDeletePlan ? (
                    <div className="cursor" onClick={() => onDeletePlan(item)}>
                      <div className="flex-center">
                        <DeleteOutlineIcon sx={delBtn} />
                      </div>
                      <Typography className="red3 fs8 txt-semi-bold text-align-center">
                        Delete
                      </Typography>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div>
        <div className="tp-schedule-header">
          <h2 className="tp-section-title">
            {moment(selectedDate).isSame(moment(), "day")
              ? `Today's schedule · ${dateLabel}`
              : `Schedule · ${dateLabel}`}
          </h2>
          <div className="tp-schedule-date-picker flex-row-ali-cen">
            <NavigateBeforeOutlinedIcon
              className="cal-arrow cursor"
              onClick={() => handleDateChange("minus")}
            />
            <div
              className="mh10 cal-date-label-picker"
              style={{
                position: "relative",
                display: "inline-block",
              }}
            >
              <Typography className="text-bold day-type-box black ph10">
                {dateLabel}
              </Typography>
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  opacity: 0,
                  overflow: "hidden",
                  cursor: "pointer",
                }}
              >
                <CustomTextField
                  mobileDate
                  name="selectedDate"
                  value={selectedDate}
                  handleChange={onChangeSelectedDate}
                  fullWidth
                />
              </div>
            </div>
            <NavigateNextOutlinedIcon
              className="cal-arrow cursor"
              onClick={() => handleDateChange("add")}
            />
          </div>
        </div>

        <div className="tp-schedule-list">
          {scheduleEntries.map((entry) => (
            <div
              key={entry.id}
              className={`tp-sched-row ${entry.status === "OVERDUE" ? "overdue-row" : ""}`}
            >
              <div className="tp-sched-time">{entry.time}</div>
              <div className="tp-sched-name">
                <div>{entry.name}</div>
                {entry.status === "GIVEN" &&
                (entry.doneBy || entry.doneTime) ? (
                  <div className="tp-sched-done-by">
                    {[
                      entry.doneBy ? `Done by ${entry.doneBy}` : null,
                      entry.doneTime && moment(entry.doneTime).isValid()
                        ? moment(entry.doneTime).format("DD MMM YYYY, HH:mm")
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                ) : null}
                {entry.status === "NOT_DONE" &&
                (entry.remark || entry.doneBy || entry.doneTime) ? (
                  <div className="tp-sched-done-by">
                    {[
                      entry.doneBy ? `Done by ${entry.doneBy}` : null,
                      entry.doneTime && moment(entry.doneTime).isValid()
                        ? moment(entry.doneTime).format("DD MMM YYYY, HH:mm")
                        : null,
                      entry.remark ? `Reason: ${entry.remark}` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                ) : null}
              </div>
              <div className="tp-sched-actions">
                {entry.status === "OVERDUE" && (
                  <span className="badge-overdue">Overdue</span>
                )}
                {entry.status === "DUE" && (
                  <span className="badge-due">Due</span>
                )}
                {entry.status === "GIVEN" && (
                  <span className="badge-given">Done</span>
                )}
                {entry.status === "NOT_DONE" && (
                  <span className="badge-not-done">Not Done</span>
                )}

                {canManageDashboard &&
                  entry.status !== "GIVEN" &&
                  entry.status !== "NOT_DONE" && (
                    <div className="flex-row-ali-cen">
                      <div>
                        <CustomButton
                          text="Mark Done"
                          smallBtn
                          onClick={() =>
                            updateTaskStatus(entry.raw || entry, true)
                          }
                        />
                      </div>
                      <div className="ml10">
                        <CustomButton
                          text="Not Done"
                          smallBtn
                          redBtn
                          onClick={() => handleTaskCancel(entry.raw || entry)}
                        />
                      </div>
                      {canDeletePlan ? (
                        <div
                          className="cursor ml10"
                          onClick={() => onDeleteSubTask(entry)}
                        >
                          <div className="flex-center">
                            <DeleteOutlineIcon sx={delBtn} />
                          </div>
                          <Typography className="red3 fs8 txt-semi-bold text-align-center">
                            Delete
                          </Typography>
                        </div>
                      ) : null}
                    </div>
                  )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <CustomModal
        open={remarksModVsble}
        onClose={onCloseRemarksMod}
        header="Reason"
        modal
        modalWidth={30}
        headerShown={true}
      >
        <div className="ph10">
          <CustomTextField
            value={remarks}
            handleChange={(e) => setRemarks(e?.target?.value)}
            multiline
            fullWidth
          />
        </div>
        <div className="flex-center">
          <div className="w20Per mv20">
            <CustomButton text="Save" smallBtn onClick={onSaveRemarks} />
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={discontinueModVsble}
        onClose={onCloseDiscontinueMod}
        header="Discontinue"
        modal
        modalWidth={40}
      >
        <p
          style={{
            fontFamily: "Montserrat",
            fontSize: 16,
            margin: "0 0 0 10px",
          }}
        >
          Are you sure you want to discontinue the task?
        </p>

        <div className="flex-center mv20">
          <div className="mr10">
            <CustomButton
              text="Yes"
              redBtn
              onClick={onConfirmDiscontinue}
              smallBtn
            />
          </div>
          <div className="ml10">
            <CustomButton text="No" onClick={onCloseDiscontinueMod} smallBtn />
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={deleteModVsble}
        onClose={onCloseDeleteMod}
        header="Delete"
        modal
        modalWidth={40}
      >
        <p
          style={{
            fontFamily: "Montserrat",
            fontSize: 16,
            margin: "0 0 0 10px",
          }}
        >
          Are you sure you want to delete{" "}
          {deleteType === "subtask"
            ? "this scheduled task?"
            : "this plan item and its pending tasks?"}
        </p>

        <div className="flex-center mv20">
          <div className="mr10">
            <CustomButton
              text="Yes"
              redBtn
              onClick={onConfirmDelete}
              smallBtn
            />
          </div>
          <div className="ml10">
            <CustomButton text="No" onClick={onCloseDeleteMod} smallBtn />
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={showAddModal}
        onClose={closeAddModal}
        modal
        headerShown
        headerCenter={false}
        header={editingTask ? "Edit Plan" : "Add to treatment plan"}
        modalWidth={45}
      >
        <div className="tp-modal-scroll" style={{ padding: "8px 0" }}>
          <div style={{ marginBottom: 14 }}>
            <label className="modal-field-label">TYPE</label>
            <CustomSelect
              select
              list={typeOptions}
              value={addForm.type}
              label=""
              handleChange={handleTypeChange}
            />
          </div>

          {renderTypeSpecificFields()}

          {/* Common fields for all types */}
          <Grid container spacing={2} style={{ marginBottom: 14 }}>
            <Grid item xs={12} sm={6}>
              <label className="modal-field-label">FREQUENCY</label>
              <CustomSelect
                select
                list={frequncyCheckList}
                value={addForm.frequency}
                handleChange={(e) =>
                  onChangeForm("frequency", e?.target?.value)
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <label className="modal-field-label">START</label>
              <CustomTextField
                fullWidth
                name="startDateTime"
                value={addForm.startDateTime}
                mobileDateTime
                is12HourFomat={false}
                handleChange={(e) =>
                  onChangeForm("startDateTime", e?.target?.value)
                }
              />
            </Grid>
          </Grid>

          <div style={{ marginBottom: 14 }}>
            <label className="modal-field-label">DURATION</label>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={addForm.duration === "Days" ? 6 : 12}>
                <CustomSelect
                  select
                  list={durationTypeOptions}
                  value={addForm.duration}
                  handleChange={(e) => {
                    const value = e?.target?.value;
                    setAddForm((prev) => ({
                      ...prev,
                      duration: value,
                      tillDiscontinued: value === "Until discontinued",
                      durationDays:
                        value === "Days" ? prev.durationDays || "" : "",
                    }));
                  }}
                />
              </Grid>
              {addForm.duration === "Days" ? (
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    fullWidth
                    name="durationDays"
                    type="number"
                    placeholder="No. of days"
                    value={addForm.durationDays}
                    handleChange={(e) => {
                      const value = e?.target?.value;
                      // Allow only positive whole numbers
                      if (value === "" || /^\d+$/.test(String(value))) {
                        onChangeForm("durationDays", value);
                      }
                    }}
                  />
                </Grid>
              ) : null}
            </Grid>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label className="modal-field-label">NOTES</label>
            <CustomTextField
              fullWidth
              name="notes"
              multiline
              rows={3}
              placeholder="Optional"
              value={addForm.notes}
              handleChange={(e) => onChangeForm("notes", e?.target?.value)}
            />
          </div>

          <div className="modal-footer-actions">
            <div>
              <CustomButton
                text="Cancel"
                smallBtn
                onClick={closeAddModal}
                redBtn
              />
            </div>
            <div>
              <CustomButton
                text={editingTask ? "Update plan" : "Add to plan"}
                smallBtn
                onClick={handleAddTreatmentPlan}
              />
            </div>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default TreatmentPlan;
